import { useState, useEffect, useRef } from 'react';
import type { Province } from '../types/province';

/**
 * Hook that provides an estimated current tick based on server-provided timing.
 *
 * Uses the server's tick interval and start timestamp to estimate the current tick
 * client-side, updating automatically as time passes. This allows the game time
 * display to update without requiring a server round-trip.
 *
 * The estimated tick resyncs with the server whenever province data is refreshed.
 *
 * @param province The province data containing tick timing information
 * @returns The estimated current game tick
 */
export function useGameTime(province: Province | null): number {
  const [estimatedTick, setEstimatedTick] = useState(province?.sync?.currentTick ?? 0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!province?.sync) {
      return;
    }

    // SF-189: a paused age's tick clock is frozen server-side -- extrapolating forward from
    // currentTickStartedAt using real wall-clock elapsed time would otherwise make the displayed
    // tick drift arbitrarily far ahead of the actually-frozen world. Short-circuit to the raw,
    // un-extrapolated currentTick instead; no interval/timeout is scheduled while paused.
    if (province.sync.paused) {
      setEstimatedTick(province.sync.currentTick);
      return;
    }

    const tickStartMs = new Date(province.sync.currentTickStartedAt).getTime();
    const intervalMs = province.sync.tickIntervalMs;

    const calculateTick = () => {
      const elapsed = Date.now() - tickStartMs;
      const ticksElapsed = Math.floor(elapsed / intervalMs);
      setEstimatedTick(province.sync.currentTick + ticksElapsed);
    };

    // Calculate immediately
    calculateTick();

    // Calculate time until next tick boundary for more accurate updates
    const elapsed = Date.now() - tickStartMs;
    const msUntilNextTick = intervalMs - (elapsed % intervalMs);

    // Set up initial timeout to align with tick boundary, then interval
    const timeoutId = setTimeout(() => {
      calculateTick();
      // After aligning to tick boundary, update at regular intervals
      intervalRef.current = setInterval(calculateTick, intervalMs);
    }, msUntilNextTick);

    return () => {
      clearTimeout(timeoutId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [province?.sync?.currentTick, province?.sync?.currentTickStartedAt, province?.sync?.tickIntervalMs, province?.sync?.paused]);

  return estimatedTick;
}
